import { useQuery } from '@tanstack/react-query';

import useApi from '@/hooks/useApi/useApi';
import { API_URL } from '@/lib/apiUrl';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import { Project, userData } from '@/types/types';

export interface Notification {
  id: number;
  type: string;
  read_at: string | null;
  seen_at: string | null;
  created_at: string;
}

export interface Account extends Notification {
  type: 'AccountReminderNotifier::Notification';
  params: {
    message: string;
  };
}

export interface ProjectStart extends Notification {
  type: 'ProjectStartReminderNotifier::Notification';
  params: {
    project?: Project;
  };
}

export interface task extends Notification {
  type: 'TaskNotifier::Notification';
  params: {
    project?: Project;
    assigned_by: userData;
  };
}

export interface Timesheet extends Notification {
  type: 'TimesheetReminderNotifier::Notification';
  params: {
    week_ending: string;
  };
}

export interface TimesheetApproval extends Notification {
  type: 'TimesheetApprovalNotifier::Notification';
  params: {
    timesheet: {
      work_date: string;
    };
  };
}

export interface TimesheetRejection extends Notification {
  type: 'TimesheetRejectionNotifier::Notification';
  params: {
    timesheet: {
      work_date: string;
    };
  };
}

export interface Feedback extends Notification {
  type: 'FeedbackNotifier::Notification';
  params: {
    feedback_type: 'eNPS' | 'CES' | 'CSAT';
  };
}

export interface ProjectOverdue extends Notification {
  type: 'ProjectOverdueNotifier::Notification';
  params: {
    project?: Project;
  };
}

export interface ProjectEndReminder extends Notification {
  type: 'ProjectEndReminderNotifier::Notification';
  params: {
    project?: Project;
  };
}

export interface IntervalCompletionStateSubmitted extends Notification {
  type: 'IntervalCompletionStateSubmittedNotifier::Notification';
  params: {
    project?: Project;
    resource_name: string;
    percentage: number;
  };
}

export interface IntervalCommentAdded extends Notification {
  type: 'IntervalCommentAddedNotifier::Notification';
  params: {
    project?: Project;
    interval_comment?: {
      id: number;
      interval_id: number;
      author_id: number;
      body: string;
    };
  };
}

export interface NotificationsResponse {
  notifications: (
    | Account
    | Timesheet
    | TimesheetApproval
    | TimesheetRejection
    | ProjectStart
    | task
    | Feedback
    | ProjectOverdue
    | ProjectEndReminder
    | IntervalCompletionStateSubmitted
    | IntervalCommentAdded
  )[];
  unread_count: number;
}

export function useNotifications(accountId?: string) {
  const { userData } = useAuth();
  const id = accountId || userData?.id;
  const { axiosInstance } = useApi();

  return useQuery<NotificationsResponse>({
    queryKey: ['notifications', id],

    queryFn: async () => {
      const { data } = await axiosInstance.get<NotificationsResponse>(
        `${API_URL}/api/v1/accounts/${id}/notifications`
      );
      return data;
    },
    enabled: !!id,
    staleTime: 60_000, // 1 minute
  });
}
